import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import instance from "services/axiosServices";

const BASE_PATH = "whatsapp/common/whatsapp";
const QUERY_KEY = "wa-templates";

export interface TemplateAPI {
  id: string;
  nome: string;
  conteudo: string;
  templatePadrao?: boolean;
  oculto?: boolean;
}

/**
 * GET /whatsapp/common/whatsapp/template/v1
 * ?apenasTemplatesPadrao=false (traz todos)
 */
export function useWhatsAppTemplates(apenasTemplatesPadrao = false) {
  return useQuery<TemplateAPI[]>({
    queryKey: [QUERY_KEY, apenasTemplatesPadrao],
    queryFn: async () => {
      const response = await instance.get(`${BASE_PATH}/template/v1`, {
        params: { apenasTemplatesPadrao },
      });
      const data = response.data?.data;
      return Array.isArray(data) ? data : data?.templates ?? [];
    },
    refetchOnWindowFocus: false,
  });
}

/**
 * POST /whatsapp/common/whatsapp/template/v1
 * Body: { nome, conteudo }
 */
export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { nome: string; conteudo: string }) => {
      const response = await instance.post(`${BASE_PATH}/template/v1`, body);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success("Template criado com sucesso.");
    },
    onError: (error: any) => {
      const notifications = error?.response?.data?.notifications;
      const msg = Array.isArray(notifications)
        ? notifications.map((n: any) => n?.message).join("\n")
        : "Erro ao criar template";
      toast.error(msg);
    },
  });
}

/**
 * PUT /whatsapp/common/whatsapp/template/v1/{id}
 * Body: { nome, conteudo }
 */
export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      id: string;
      nome: string;
      conteudo: string;
    }) => {
      const response = await instance.put(
        `${BASE_PATH}/template/v1/${body.id}`,
        { nome: body.nome, conteudo: body.conteudo }
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success("Template atualizado.");
    },
    onError: (error: any) => {
      const notifications = error?.response?.data?.notifications;
      const msg = Array.isArray(notifications)
        ? notifications.map((n: any) => n?.message).join("\n")
        : "Erro ao atualizar template";
      toast.error(msg);
    },
  });
}

/**
 * DELETE /whatsapp/common/whatsapp/template/v1/{id}
 */
export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await instance.delete(`${BASE_PATH}/template/v1/${id}`);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success("Template excluído.");
    },
    onError: (error: any) => {
      const notifications = error?.response?.data?.notifications;
      const msg = Array.isArray(notifications)
        ? notifications.map((n: any) => n?.message).join("\n")
        : "Erro ao excluir template";
      toast.error(msg);
    },
  });
}

/**
 * POST /whatsapp/common/whatsapp/template/v1/{id}/restaurar
 */
export function useRestoreTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await instance.post(
        `${BASE_PATH}/template/v1/${id}/restaurar`
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success("Template restaurado.");
    },
    onError: (error: any) => {
      const notifications = error?.response?.data?.notifications;
      const msg = Array.isArray(notifications)
        ? notifications.map((n: any) => n?.message).join("\n")
        : "Erro ao restaurar template";
      toast.error(msg);
    },
  });
}
